/**
 * Pure calculation functions extracted from payout/utils.ts and ordersCreateEvent.ts
 * for testability. These have no external dependencies.
 */
import { CommissionBasis, Deduction, DeductionType } from '../settings/types';

export enum PeriodType {
  Today = 'TODAY',
  Last7Days = 'LAST_7_DAYS',
  Last30Days = 'LAST_30_DAYS',
  Last90Days = 'LAST_90_DAYS',
  Last180Days = 'LAST_180_DAYS',
  Last365Days = 'LAST_365_DAYS',
  Custom = 'CUSTOM',
}

export type DateRange = {
  start: string;
  end: string;
};

/**
 * Returns a date range { start, end } as ISO strings for the given period.
 * Returns null for Custom or unrecognized periods.
 */
export function getDateRangeFromPeriod(period: string, now?: Date): DateRange | null {
  const referenceDate = now ?? new Date();
  const end = referenceDate.toISOString();
  let start: Date;

  switch (period) {
    case PeriodType.Today:
      start = new Date(referenceDate);
      start.setHours(0, 0, 0, 0);
      break;
    case PeriodType.Last7Days:
      start = new Date(referenceDate);
      start.setDate(referenceDate.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case PeriodType.Last30Days:
      start = new Date(referenceDate);
      start.setDate(referenceDate.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
    case PeriodType.Last90Days:
      start = new Date(referenceDate);
      start.setDate(referenceDate.getDate() - 89);
      start.setHours(0, 0, 0, 0);
      break;
    case PeriodType.Last180Days:
      start = new Date(referenceDate);
      start.setDate(referenceDate.getDate() - 179);
      start.setHours(0, 0, 0, 0);
      break;
    case PeriodType.Last365Days:
      start = new Date(referenceDate);
      start.setDate(referenceDate.getDate() - 364);
      start.setHours(0, 0, 0, 0);
      break;
    default:
      return null;
  }

  return { start: start.toISOString(), end };
}

export type CalculatedProductCommission = {
  percentage: number;
  amount: number;
  basis?: CommissionBasis;
  base?: number;
  unitCost?: number;
};

/**
 * Calculates the commission amount for a product line.
 *
 * `price` is the UNIT price and `options.quantity` the number of units on the line
 * (default 1). The commission scales with quantity: a 2 x $50 line at 10% owes $10, not
 * $5 (MPPRO-78). `amount` and `base` are therefore LINE-level, so
 * `base * percentage / 100 === amount` holds and the figure can be read straight into a
 * fee row beside the line total. `unitCost` stays PER UNIT — its name says so, and the
 * order screens print it as "unit price less $X cost".
 *
 * With a PROFIT basis the percentage is charged on (price - unit cost) rather than the
 * price. Three rules make that safe:
 *
 *  - **No cost -> NET_SALES.** A product with no cost (merchant hasn't set one, or the app
 *    lacks Shopify's "View product costs" permission) silently falls back to charging on
 *    price. Order creation is a webhook handler; a missing cost must never throw.
 *  - **Never negative.** If cost exceeds price the profit is clamped to 0 — PER UNIT, and
 *    before multiplying by quantity. Clamping the line total instead would let one unit's
 *    negative profit cancel another's; left unclamped entirely a negative commission would
 *    *reduce* totalFees and so silently INCREASE the payout.
 *  - **The basis actually applied is returned**, so a fallback is visible in the snapshot
 *    rather than looking like a configured NET_SALES charge.
 *
 * Like the rest of the fee math this does not round — see calculateDeductions.
 */
export function calculateProductCommission(
  price: number,
  productPercentage: number,
  options?: { basis?: CommissionBasis; unitCost?: number; quantity?: number }
): CalculatedProductCommission {
  const wantsProfit = options?.basis === CommissionBasis.Profit;
  const unitCost = options?.unitCost;
  const hasCost = wantsProfit && unitCost != null;
  const quantity = options?.quantity ?? 1;

  const basis = hasCost ? CommissionBasis.Profit : CommissionBasis.NetSales;
  const unitBase = hasCost ? Math.max(0, price - unitCost!) : price;
  const base = unitBase * quantity;
  const amount = base * (productPercentage / 100);

  return {
    percentage: productPercentage,
    amount,
    basis,
    base,
    ...(hasCost ? { unitCost } : {}),
  };
}

export type CalculatedLineItemFees = {
  /** Line-level commission, ready to snapshot onto the order item. */
  commission: CalculatedProductCommission;
  /** The flat product fee EXTENDED across the line: unit fee x quantity. */
  productFee: number;
  /** commission.amount + productFee. */
  totalFees: number;
};

/**
 * Calculates every fee a single order line owes — the one implementation shared by the
 * order-creation webhook and the seller-facing "You receive" estimate (MPPRO-79), so the
 * number a vendor is quoted cannot drift from the number they are charged.
 *
 * Both charges scale with quantity: the percentage via calculateProductCommission, and
 * the flat product fee because it is charged PER UNIT (MPPRO-78). A $2 flat fee on a
 * 2 x $50 line is $4.
 *
 * This replaces the former `calculateItemTotalFees`, which no caller used and which had
 * no notion of quantity at all. The order-level formula that used to sit beside it as
 * `calculateSellerOrderPayout` is likewise gone: it was unused and had drifted from the
 * live snapshot, omitting both the shipping commission and the S9 deductions that
 * ordersCreateEvent actually applies. The live order math stays in `createSellerOrder`,
 * its only caller.
 */
export function calculateLineItemFees(args: {
  /** Unit price in cents. */
  unitPrice: number;
  quantity: number;
  productPercentage: number;
  /** The flat product fee setting, in cents, PER UNIT. */
  productFee: number;
  basis?: CommissionBasis;
  /** Unit cost in cents, for a PROFIT basis. */
  unitCost?: number;
}): CalculatedLineItemFees {
  const { unitPrice, quantity, productPercentage, productFee, basis, unitCost } = args;

  const commission = calculateProductCommission(unitPrice, productPercentage, {
    basis,
    unitCost,
    quantity,
  });
  const extendedProductFee = productFee * quantity;

  return {
    commission,
    productFee: extendedProductFee,
    totalFees: commission.amount + extendedProductFee,
  };
}

export type PayoutFee = {
  percentageFee: number;
  flatFee: number;
  calculatedAmount: number;
};

/**
 * Calculates a provider fee in WHOLE CENTS: round((amount * percentageFee / 100) + flatFee).
 *
 * MPPRO-130. This is the one place a payout provider fee is rounded, and rounding
 * belongs here rather than at storage or display for a simple reason: the result is
 * not only shown to a merchant, it is *charged*. `calculatePayoutTransaction` adds it
 * straight onto `from.amount`, which reaches `stripe.transfers.create({ amount })` and
 * the PayPal payouts API — both of which take integer minor units and reject anything
 * else. A fractional fee is therefore an invalid transfer, not just an ugly number.
 *
 * Rounding at display would leave the invalid figure in the stored payout record and in
 * the amount actually sent; rounding at both ends would be two rounding sites for one
 * quantity, which is the defect this replaces rather than a fix for it. So: round once,
 * at the point the fee comes into existence. Storage and display take the integer as
 * given — the admin UI's `formatCents` only renders cents as dollars, it decides nothing.
 *
 * Concretely, the case that opened MPPRO-130: a $12.34 payout (1234 cents) through
 * Stripe used to yield 1234 * 2.9 / 100 + 25 = 60.786 cents, which was stored on the
 * payout record and rendered in the UI. It is now 61.
 *
 * `amount` is expected in integer cents (MPPRO-44 / MPPRO-127).
 */
export function calculateProviderFee(
  amount: number,
  percentageFee: number,
  flatFee: number
): number {
  return Math.round((amount * percentageFee) / 100 + flatFee);
}

/** Stripe fee constants as used in payout/utils.ts. `flatFee` is integer cents. */
export const STRIPE_FEE = { percentageFee: 2.9, flatFee: 25 };
export const STRIPE_CONVERSION_FEE = { percentageFee: 1, flatFee: 0 };

/** PayPal fee constants as used in payout/utils.ts. `flatFee` is integer cents. */
export const PAYPAL_FEE = { percentageFee: 2.9, flatFee: 30 };
export const PAYPAL_CONVERSION_FEE = { percentageFee: 2.5, flatFee: 0 };

export type PayoutTransactionAmounts = {
  /** The provider's own fee, whole cents. */
  providerFeeAmount: number;
  /** The currency-conversion fee, whole cents. */
  conversionFeeAmount: number;
  /** What the shop pays: totalAmount + both fees. */
  fromAmount: number;
  /** What the seller receives: totalAmount. */
  toAmount: number;
};

/**
 * Calculates the payout transaction amounts for a given provider.
 *
 * from = totalAmount + providerFee + conversionFee (what the shop pays)
 * to   = totalAmount                               (what the seller receives)
 *
 * The two fees are returned alongside the totals because the payout request stores
 * and displays them individually, as a breakdown that must add up to `fromAmount -
 * toAmount`. Since each is rounded to whole cents by `calculateProviderFee`, and
 * `totalAmount` is already whole cents, `fromAmount` is whole cents too — no second
 * rounding is applied to it, and none is needed.
 */
export function calculatePayoutTransaction(
  totalAmount: number,
  providerFee: { percentageFee: number; flatFee: number },
  conversionFee: { percentageFee: number; flatFee: number }
): PayoutTransactionAmounts {
  const providerFeeAmount = calculateProviderFee(totalAmount, providerFee.percentageFee, providerFee.flatFee);
  const conversionFeeAmount = calculateProviderFee(totalAmount, conversionFee.percentageFee, conversionFee.flatFee);
  return {
    providerFeeAmount,
    conversionFeeAmount,
    fromAmount: totalAmount + providerFeeAmount + conversionFeeAmount,
    toAmount: totalAmount,
  };
}

/**
 * Calculates the final payout amount after adjustment.
 * calculatedPayoutAmount = totalAmount + adjustmentAmount
 *
 * Deliberately unrounded: both operands are already whole cents (the sum of
 * `SellerOrder.totalPayout`, and a merchant-entered adjustment), so there is nothing
 * here to round. Rounding it anyway would be a second rounding site for a quantity
 * that never acquires a fraction.
 */
export function calculateAdjustedPayout(
  totalAmount: number,
  adjustmentAmount: number
): number {
  return totalAmount + adjustmentAmount;
}

/**
 * Calculates the shipping commission based on shipping cost and percentage.
 */
export function calculateShippingCommission(
  shippingCost: number,
  shippingPercentage: number
): { amount: number; percentage: number } {
  const amount = shippingCost * (shippingPercentage / 100);
  return { amount, percentage: shippingPercentage };
}

/**
 * Allocates total order shipping cost to a seller proportionally based on item value.
 */
export function calculateSellerShippingAllocation(
  sellerItemTotal: number,
  orderTotal: number,
  totalShipping: number
): number {
  if (orderTotal === 0) return 0;
  return totalShipping * (sellerItemTotal / orderTotal);
}

/**
 * Applies a list of named deductions to a seller's order subtotal.
 *
 * PERCENTAGE deductions are computed on the ORIGINAL `sellerSubtotal`, so they are
 * order-independent — the Nth deduction never depends on the previous ones. FLAT
 * deductions are taken as-is. No rounding: this matches the rest of the fee math,
 * which is float-valued in cents (only the Shopify boundary rounds; see money.ts).
 *
 * Returns the total deducted plus an itemised breakdown to snapshot on the SellerOrder.
 */
export function calculateDeductions(
  sellerSubtotal: number,
  deductions: Deduction[]
): { total: number; applied: Array<{ name: string; type: DeductionType; rate: number; amount: number }> } {
  const applied = (deductions || []).map((d) => {
    const amount =
      d.type === DeductionType.Percentage
        ? sellerSubtotal * (d.amount / 100)
        : d.amount;
    return { name: d.name, type: d.type, rate: d.amount, amount };
  });
  const total = applied.reduce((sum, a) => sum + a.amount, 0);
  return { total, applied };
}

/**
 * One seller order's snapshot, as far as refund arithmetic is concerned. Every field is
 * the value `ordersCreateEvent.createSellerOrder` wrote at order creation, in cents.
 */
export type SellerOrderRefundInput = {
  items: Array<{
    /** Unit price in cents, as snapshotted. */
    unitPrice: number;
    /** Units originally ordered on this line. */
    quantity: number;
    /** Line-level fees charged on this line: commission + extended product fee. */
    totalFees: number;
    /** Units of this line already refunded by an earlier refund. */
    refundedQuantity?: number;
  }>;
  /** Units being refunded now, positionally aligned with `items`. */
  refundedQuantities: number[];
  /** The seller order's merchandise subtotal: sum of item.total. */
  total: number;
  /** The flat per-order fee charged to this seller. */
  orderFee: number;
  /** The shipping commission amount charged to this seller, 0 when none. */
  shippingCommissionAmount: number;
  /** The sum of the S9 named deductions applied to this seller order. */
  deductionsTotal: number;
};

export type CalculatedSellerOrderRefund = {
  /** Units actually applied per line, clamped to what was still unrefunded. */
  appliedQuantities: number[];
  /** Merchandise value refunded, in cents. */
  refundedMerchandise: number;
  /** The refunded lines' own fees, reversed pro rata. */
  lineFeesReversed: number;
  /** Order-level charges reversed, allocated by refunded merchandise share. */
  orderChargesReversed: number;
  /** What to subtract from `SellerOrder.totalPayout`. */
  payoutReduction: number;
};

/**
 * Works out how much of a Shopify refund comes out of one seller's payout (MPPRO-137).
 *
 * This is the order-creation formula run backwards, not a second calculation. Order
 * creation charged the seller two kinds of thing, and each is reversed the way it was
 * charged:
 *
 *  - **Line-level fees** — the product commission and the flat product fee — were both
 *    computed PER UNIT and multiplied by the line quantity (`calculateLineItemFees`, and
 *    MPPRO-78 for the quantity scaling). So `item.totalFees` is exactly linear in
 *    quantity, and `item.totalFees * refunded / quantity` is the same per-unit charge
 *    the seller originally paid on the units coming back. Reading it off the snapshot
 *    also means a later settings change cannot rewrite a historical refund, which is
 *    the reason the snapshot exists at all.
 *  - **Order-level charges** — the flat `orderFee`, the shipping commission and the S9
 *    deductions — are properties of the order rather than of any line, so they are
 *    allocated by the refunded share of merchandise. That is the same allocate-by-value
 *    rule `calculateSellerShippingAllocation` already uses to split order shipping
 *    across sellers, applied to the same quantity from the other direction.
 *
 * The consequence worth stating: refunding EVERY unit of a seller order reduces its
 * payout by exactly `total - totalFees`, which is `totalPayout` — so a fully refunded
 * seller order is worth zero and can never be driven negative by its own refund. That
 * matches what a cancellation does (a cancelled seller order leaves the payable pool
 * entirely) and is what makes the "refund exceeds the balance" case tractable: within
 * one seller order it cannot arise.
 *
 * Quantities are clamped to what is still unrefunded, so an at-least-once redelivery or
 * a malformed payload cannot refund eleven of ten units. The caller reports a clamp; it
 * is a signal that something upstream is wrong, not a routine adjustment.
 *
 * Like the rest of the fee math this does not round — see `calculateDeductions`. Cents
 * here are float-valued, and rounding happens only where money crosses a provider
 * boundary (`calculateProviderFee`).
 */
export function calculateSellerOrderRefund(
  args: SellerOrderRefundInput
): CalculatedSellerOrderRefund {
  const { items, refundedQuantities, total, orderFee, shippingCommissionAmount, deductionsTotal } =
    args;

  const appliedQuantities = items.map((item, index) => {
    const requested = refundedQuantities[index] ?? 0;
    if (requested <= 0) return 0;
    const remaining = Math.max(0, item.quantity - (item.refundedQuantity ?? 0));
    return Math.min(requested, remaining);
  });

  let refundedMerchandise = 0;
  let lineFeesReversed = 0;
  items.forEach((item, index) => {
    const quantity = appliedQuantities[index];
    if (quantity <= 0) return;
    refundedMerchandise += item.unitPrice * quantity;
    // item.quantity is never 0 for a real order line; guard anyway so a malformed
    // snapshot yields no fee reversal rather than NaN propagating into a payout.
    if (item.quantity > 0) {
      lineFeesReversed += (item.totalFees * quantity) / item.quantity;
    }
  });

  const orderCharges = orderFee + shippingCommissionAmount + deductionsTotal;
  const orderChargesReversed =
    total > 0 ? orderCharges * (refundedMerchandise / total) : 0;

  return {
    appliedQuantities,
    refundedMerchandise,
    lineFeesReversed,
    orderChargesReversed,
    payoutReduction: refundedMerchandise - lineFeesReversed - orderChargesReversed,
  };
}
